import * as React from 'react'
import { useNewTrip } from '@/modules/drivers/context/NewTripContext'
import { EventModel } from '@/app/models/newDelegation/eventModel'
import { TripEventType } from '@/app/constants/delegation/tripEventType'
import { FaIcon, faArrowLeft, faTriangleExclamationSolid } from '@/lib/fa'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import BusinessTripEditEvents from '@/modules/drivers/tabs/businessTrips/editBusinessTrip/tabs/events/BusinessTripEditEvents'
import { ActivityModel } from '@/modules/drivers/types/activities'
import { applyEventTableUpdate } from '@/modules/drivers/utils/eventTmsPair'
import { removeTripEvents } from '@/modules/drivers/utils/eventListEdits'
import {
  withVehicleFromActivities,
  withVehicleOnEdit,
} from '@/modules/drivers/utils/eventVehicle'

// Mirrors server-side ValidateTripItems: marks which event indices are wrong
const getWrongEventIndices = (events: EventModel[]): Set<number> => {
  const wrongIndices = new Set<number>()
  const sorted = [...events]
    .map((e, i) => ({ e, i }))
    .sort((a, b) => {
      const da = a.e.date ? new Date(a.e.date as any).getTime() : 0
      const db = b.e.date ? new Date(b.e.date as any).getTime() : 0
      return da - db
    })

  let beginIndex: number | null = null
  for (const { e, i } of sorted) {
    if (!e.date) {
      wrongIndices.add(i)
    }
    if (e.tripEvent === TripEventType.Begin) {
      if (beginIndex !== null) {
        wrongIndices.add(i)
        return wrongIndices
      }
      beginIndex = i
    }
    if (e.tripEvent === TripEventType.Finish) {
      if (beginIndex === null) {
        wrongIndices.add(i)
        return wrongIndices
      }
      beginIndex = null
    }
  }
  if (beginIndex !== null) {
    wrongIndices.add(beginIndex)
  }
  return wrongIndices
}

interface EventsErrorModalProps {
  companyId: string
  driverId: string
  showError?: boolean
  activities?: ReadonlyArray<ActivityModel>
  onClose: () => void
}

export const EventsErrorModal: React.FC<EventsErrorModalProps> = ({
  companyId,
  driverId,
  showError,
  activities,
  onClose,
}) => {
  const { t } = useLocalize()
  const { events, addEvent, resetEvents } = useNewTrip()

  const wrongIndices = React.useMemo(
    () => (showError ? getWrongEventIndices(events) : new Set<number>()),
    [showError, events],
  )

  // Adapter: wrap events in tripData format expected by BusinessTripEditEvents
  const tripData = React.useMemo(() => ({ events }), [events])

  const onUpdate = React.useCallback(
    (field: string, value: any) => {
      if (field === 'events') {
        resetEvents(value as EventModel[])
      }
    },
    [resetEvents],
  )

  const onUpdateArrayItem = React.useCallback(
    (arrayName: string, index: number, updates: any, baseline?: any) => {
      if (arrayName === 'events' && events[index]) {
        resetEvents(
          applyEventTableUpdate(events, index, updates, baseline).map((e, i) =>
            i === index ? withVehicleOnEdit(e, events[index], activities) : e,
          ),
        )
      }
    },
    [events, activities, resetEvents],
  )

  const onAddArrayItem = React.useCallback(
    (arrayName: string, newItem: any) => {
      if (arrayName === 'events') {
        addEvent(withVehicleFromActivities(newItem as EventModel, activities))
      }
    },
    [activities, addEvent],
  )

  const onRemoveArrayItems = React.useCallback(
    (arrayName: string, indices: number[]) => {
      if (arrayName === 'events') {
        const toRemove = indices.map((i) => events[i]).filter(Boolean)
        resetEvents(removeTripEvents(events, toRemove))
      }
    },
    [events, resetEvents],
  )

  const validationErrors = React.useMemo(() => {
    if (!showError || wrongIndices.size === 0) return {}
    return {
      events:
        t('errorCode.MISSING_BEGIN_OR_END'),
    }
  }, [showError, wrongIndices, t])

  return (
    <div className="tw-absolute tw-inset-0 tw-z-50 tw-flex tw-flex-col tw-bg-white">
      {/* Header */}
      <div className="tw-flex tw-items-center tw-gap-3 tw-border-b tw-border-base-300 tw-bg-[#fafbff] tw-px-4 tw-py-3">
        <button
          onClick={onClose}
          className="tw-flex tw-items-center tw-gap-1 tw-text-sm tw-text-gray-600 hover:tw-text-gray-900"
          title={t('common.back')}
        >
          <FaIcon icon={faArrowLeft} />
        </button>
        {showError && wrongIndices.size > 0 && (
          <FaIcon icon={faTriangleExclamationSolid} className="tw-text-red-500" />
        )}
        <span className="tw-font-semibold tw-text-content-base">
          {t('tab.new-delegation-event') || 'Zdarzenia'}
        </span>
      </div>

      {/* Events table */}
      <div className="tw-flex-1 tw-overflow-auto tw-p-2">
        <BusinessTripEditEvents
          tripData={tripData}
          onUpdate={onUpdate}
          onUpdateArrayItem={onUpdateArrayItem}
          onAddArrayItem={onAddArrayItem}
          onRemoveArrayItems={onRemoveArrayItems}
          companyId={companyId}
          driverId={driverId}
          tripId=""
          validationErrors={validationErrors}
          wrongIndices={wrongIndices}
        />
      </div>
    </div>
  )
}
